import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, X, Edit2, Save, Plus, User, ArrowLeft } from 'lucide-react';
import { api } from '@/services/api';
import { useAlert } from '@/components/AlertProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ExtractedData {
  order_number?: string;
  order_date?: string;
  total_amount?: number;
  currency?: string;
  buyer_company?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  billing_address?: string;
  billing_city?: string;
  billing_postal_code?: string;
  billing_country?: string;
  delivery_address?: string;
  delivery_city?: string;
  delivery_postal_code?: string;
  delivery_country?: string;
  items?: Array<{
    product_name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }>;
  notes?: string;
  quote_reference?: string;
}

interface UploadResponse {
  status: 'created' | 'review_required';
  message: string;
  extracted_data: ExtractedData;
  matched_quote_id?: number;
  order?: any;
  unmatched_items?: string[];
  pdf_text_preview?: string;
}

interface Client {
  id: number;
  company_name?: string;  // Pour les buyers
  name?: string;          // Pour les companies
}

interface Quote {
  id: number;
  quote_number: string;
  total: number;
  currency: string;
  status: string;
  created_at: string;
}

const UploadPurchaseOrderPDF: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo } = useAlert();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState<ExtractedData | null>(null);
  const [buyers, setBuyers] = useState<Client[]>([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [newBuyerName, setNewBuyerName] = useState('');
  const [showNewBuyerForm, setShowNewBuyerForm] = useState(false);
  const [creatingBuyer, setCreatingBuyer] = useState(false);
  const [_quotes, _setQuotes] = useState<Quote[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [_loadingQuotes, _setLoadingQuotes] = useState(false);

  // Charger la liste des entreprises (companies = clients)
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        const response = await api.get('/clients');
        console.log('✅ Companies loaded:', response.data.length, 'companies');
        setBuyers(response.data);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des entreprises:', error);
      }
    };
    fetchBuyers();
  }, []);

  // Synchroniser la sélection d'client/affichage formulaire selon les données extraites
  useEffect(() => {
    console.log('🔍 Auto-selection effect triggered', {
      hasResult: !!result,
      hasExtractedData: !!result?.extracted_data,
      buyersCount: buyers.length,
      status: result?.status,
      buyerCompany: result?.extracted_data?.buyer_company
    });

    if (!result || !result.extracted_data || buyers.length === 0) {
      console.log('⏭️ Skipping: missing data');
      return;
    }
    if (result.status !== 'review_required') {
      console.log('⏭️ Skipping: status is not review_required');
      return;
    }

    const name = result.extracted_data.buyer_company?.trim();
    if (!name) {
      console.log('⏭️ Skipping: no buyer_company in extracted data');
      return;
    }

    console.log('🔎 Searching for company:', name);
    console.log('📋 Available companies:', buyers.map(b => b.name || b.company_name));

    const lcName = name.toLowerCase();
    // 1) Exact match (case-insensitive) - chercher dans 'name' pour companies
    let match = buyers.find(b => (b.name || b.company_name || '').toLowerCase() === lcName);
    console.log('1️⃣ Exact match:', match?.name || match?.company_name || 'none');

    // 2) Includes match (Client contains extracted)
    if (!match) {
      match = buyers.find(b => (b.name || b.company_name || '').toLowerCase().includes(lcName));
      console.log('2️⃣ Includes match (company contains):', match?.name || match?.company_name || 'none');
    }
    // 3) Includes match (extracted contains Client)
    if (!match) {
      match = buyers.find(b => lcName.includes((b.name || b.company_name || '').toLowerCase()));
      console.log('3️⃣ Includes match (extracted contains):', match?.name || match?.company_name || 'none');
    }

    if (match) {
      console.log('✅ Company matched:', match.name || match.company_name, 'ID:', match.id);
      setSelectedBuyerId(match.id);
      setShowNewBuyerForm(false);
      setNewBuyerName('');
    } else {
      console.log('➕ No match found, showing new company form with:', name);
      // Pré-remplir le formulaire de création avec le nom détecté
      setShowNewBuyerForm(true);
      setNewBuyerName(name);
      setSelectedBuyerId(null);
    }
  }, [result, buyers]);

  // Charger les devis disponibles
  useEffect(() => {
    const fetchQuotes = async () => {
      _setLoadingQuotes(true);
      try {
        const response = await api.get('/quotes/list', {
          params: {
            status: 'sent,accepted',
            limit: 50
          }
        });
        console.log('✅ Quotes loaded:', response.data.length, 'quotes');
        _setQuotes(response.data);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des devis:', error);
      } finally {
        _setLoadingQuotes(false);
      }
    };
    if (editMode) {
      fetchQuotes();
    }
  }, [editMode]);

  // Créer un nouvel client
  const handleCreateBuyer = async () => {
    if (!newBuyerName.trim()) {
      showError("Veuillez saisir un nom d'entreprise");
      return;
    }

    setCreatingBuyer(true);
    try {
      const response = await api.post('/clients', {
        name: newBuyerName.trim()
      });

      const newCompany = response.data;
      setBuyers([...buyers, newCompany]);
      setSelectedBuyerId(newCompany.id);
      setNewBuyerName('');
      setShowNewBuyerForm(false);

      showSuccess("Entreprise créée avec succès");
    } catch (error: any) {
      showError(error.response?.data?.detail || "Erreur lors de la création de l'entreprise");
    } finally {
      setCreatingBuyer(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
      } else {
        showError("Veuillez sélectionner un fichier PDF");
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('pdf_file', file);

      const response = await api.post('/purchase-orders/upload-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      setEditedData(response.data.extracted_data);
      // Pré-sélection du devis s'il est détecté côté backend
      if (response.data.matched_quote_id) {
        setSelectedQuoteId(response.data.matched_quote_id);
      } else {
        setSelectedQuoteId(null);
      }

      if (response.data.status === 'created') {
        showSuccess("Bon de commande créé avec succès");
      } else {
        showWarning(response.data.message);
        setEditMode(true);
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || "Erreur lors de l'upload du PDF");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedBuyerId || !editedData) {
      showError("Veuillez sélectionner un client");
      return;
    }

    setCreating(true);

    try {
      const formData = new FormData();
      formData.append('pdf_file', file!);

      // Construire l'URL avec les paramètres (utiliser client_id)
      let url = `/purchase-orders/upload-pdf?client_id=${selectedBuyerId}`;
      if (selectedQuoteId) {
        url += `&quote_id=${selectedQuoteId}`;
      }

      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'created') {
        showSuccess("Bon de commande créé avec succès");
        setResult(response.data);
        setEditMode(false);
      } else {
        showError(response.data.message);
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || "Erreur lors de la création du bon de commande");
    } finally {
      setCreating(false);
    }
  };

  const handleEditField = (field: keyof ExtractedData, value: any) => {
    if (editedData) {
      setEditedData({
        ...editedData,
        [field]: value
      });
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/achat/orders')}
            className="hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <Upload className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Créer un bon de commande depuis PDF
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Upload un PDF pour extraction automatique des données
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Upload Zone */}
          {!result && (
            <div
              className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 ${dragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                  : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900'
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />

              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center">
                  <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>

                {file ? (
                  <div className="space-y-2">
                    <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {file.name}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Glissez-déposez votre PDF ici
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      ou cliquez pour sélectionner un fichier
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {file && !result && (
            <div className="flex justify-center">
              <Button
                onClick={handleUpload}
                disabled={uploading}
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mr-2" />
                    Analyser et créer
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-6">
              {/* Status Card */}
              <div className={`p-6 rounded-xl border-2 ${result.status === 'created'
                  ? 'bg-green-50 dark:bg-green-950/20 border-green-300 dark:border-green-700'
                  : 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-300 dark:border-yellow-700'
                }`}>
                <div className="flex items-start gap-4">
                  {result.status === 'created' ? (
                    <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-8 w-8 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
                      {result.message}
                    </h3>
                    {result.matched_quote_id && (
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Devis correspondant trouvé: #{result.matched_quote_id}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Extracted Data */}
              <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    Données extraites
                  </h3>
                  {result.status === 'review_required' && !editMode && (
                    <Button
                      onClick={() => setEditMode(true)}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Edit2 className="h-4 w-4" />
                      Modifier
                    </Button>
                  )}
                  {editMode && (
                    <Button
                      onClick={() => setEditMode(false)}
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Annuler
                    </Button>
                  )}
                </div>

                {editMode && result.status === 'review_required' ? (
                  <div className="space-y-4">
                    {/* Sélection de l'client */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Client *
                        </Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowNewBuyerForm(!showNewBuyerForm)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Nouvel client
                        </Button>
                      </div>

                      {showNewBuyerForm ? (
                        <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Nom de l'entreprise"
                              value={newBuyerName}
                              onChange={(e) => setNewBuyerName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleCreateBuyer();
                                }
                              }}
                            />
                            <Input
                              type="tel"
                              placeholder="Téléphone"
                              className="flex-1"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                            onClick={handleCreateBuyer}
                            disabled={creatingBuyer || !newBuyerName.trim()}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {creatingBuyer ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            onClick={() => {
                              setShowNewBuyerForm(false);
                              setNewBuyerName('');
                            }}
                            variant="ghost"
                            size="sm"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          </div>
                        </div>
                      ) : (
                        <Select value={selectedBuyerId?.toString()} onValueChange={(value) => setSelectedBuyerId(parseInt(value))}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Sélectionnez un client" />
                          </SelectTrigger>
                          <SelectContent>
                            {buyers.map((Client) => (
                              <SelectItem key={Client.id} value={Client.id.toString()}>
                                {Client.name || Client.company_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Sélection du devis (optionnel) */}
                    {/* <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Lier à un devis (optionnel)
                      </Label>
                      <Select
                        value={selectedQuoteId?.toString() || 'none'}
                        onValueChange={(value) => setSelectedQuoteId(value === 'none' ? null : parseInt(value))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Aucun devis" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun devis</SelectItem>
                          {loadingQuotes ? (
                            <SelectItem value="loading" disabled>
                              Chargement...
                            </SelectItem>
                          ) : (
                            quotes.map((quote) => (
                              <SelectItem key={quote.id} value={quote.id.toString()}>
                                {quote.quote_number} - {quote.total.toFixed(2)} {quote.currency} ({quote.status})
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div> */}

                    {/* Informations du contact */}
                    {(editedData?.contact_name || editedData?.contact_email || editedData?.contact_phone) && (
                      <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Contact détecté
                        </h4>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          {editedData.contact_name && (
                            <div>
                              <p className="text-blue-600 dark:text-blue-400 font-medium">Nom</p>
                              <p className="text-blue-900 dark:text-blue-100">{editedData.contact_name}</p>
                            </div>
                          )}
                          {editedData.contact_email && (
                            <div>
                              <p className="text-blue-600 dark:text-blue-400 font-medium">Email</p>
                              <p className="text-blue-900 dark:text-blue-100">{editedData.contact_email}</p>
                            </div>
                          )}
                          {editedData.contact_phone && (
                            <div>
                              <p className="text-blue-600 dark:text-blue-400 font-medium">Téléphone</p>
                              <p className="text-blue-900 dark:text-blue-100">{editedData.contact_phone}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Adresses */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Adresse de facturation */}
                      {(editedData?.billing_address || editedData?.billing_city || editedData?.billing_postal_code) && (
                        <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                          <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2 text-sm">
                            📄 Adresse de facturation
                          </h4>
                          <div className="space-y-1 text-sm text-amber-900 dark:text-amber-100">
                            {editedData.billing_address && <p>{editedData.billing_address}</p>}
                            <p>
                              {editedData.billing_postal_code && <span>{editedData.billing_postal_code} </span>}
                              {editedData.billing_city && <span>{editedData.billing_city}</span>}
                            </p>
                            {editedData.billing_country && <p>{editedData.billing_country}</p>}
                          </div>
                        </div>
                      )}

                      {/* Adresse de livraison */}
                      {(editedData?.delivery_address || editedData?.delivery_city || editedData?.delivery_postal_code) && (
                        <div className="bg-green-50 dark:bg-green-950/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                          <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2 text-sm">
                            🚚 Adresse de livraison
                          </h4>
                          <div className="space-y-1 text-sm text-green-900 dark:text-green-100">
                            {editedData.delivery_address && <p>{editedData.delivery_address}</p>}
                            <p>
                              {editedData.delivery_postal_code && <span>{editedData.delivery_postal_code} </span>}
                              {editedData.delivery_city && <span>{editedData.delivery_city}</span>}
                            </p>
                            {editedData.delivery_country && <p>{editedData.delivery_country}</p>}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Numéro de commande
                        </Label>
                        <Input
                          value={editedData?.order_number || ''}
                          onChange={(e) => handleEditField('order_number', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Date
                        </Label>
                        <Input
                          type="date"
                          value={editedData?.order_date || ''}
                          onChange={(e) => handleEditField('order_date', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Montant total
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedData?.total_amount || 0}
                          onChange={(e) => handleEditField('total_amount', parseFloat(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Devise
                        </Label>
                        <Input
                          value={editedData?.currency || '€'}
                          onChange={(e) => handleEditField('currency', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Numéro de commande</p>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {result.extracted_data.order_number || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Date</p>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {result.extracted_data.order_date || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Montant total</p>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {result.extracted_data.total_amount?.toFixed(2)} {result.extracted_data.currency || '€'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Entreprise</p>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {result.extracted_data.buyer_company || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Items */}
                {result.extracted_data.items && result.extracted_data.items.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
                      Articles ({result.extracted_data.items.length})
                    </h4>
                    <div className="space-y-2">
                      {result.extracted_data.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-slate-100">{item.product_name}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Qté: {item.quantity} × {item.unit_price.toFixed(2)} {result.extracted_data.currency}
                            </p>
                          </div>
                          <p className="font-bold text-slate-900 dark:text-slate-100">
                            {item.line_total.toFixed(2)} {result.extracted_data.currency}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unmatched Items Warning */}
                {result.unmatched_items && result.unmatched_items.length > 0 && (
                  <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                      Articles non trouvés dans le catalogue:
                    </p>
                    <ul className="text-sm text-yellow-700 dark:text-yellow-400 list-disc list-inside">
                      {result.unmatched_items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                    setEditMode(false);
                    setEditedData(null);
                    setSelectedBuyerId(null);
                  }}
                >
                  Analyser un autre PDF
                </Button>

                {result.status === 'review_required' && editMode && (
                  <div className="flex gap-3">
                    <Button
                      onClick={handleCreateOrder}
                      disabled={!selectedBuyerId || creating}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
                    >
                      {creating ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Création en cours...
                        </>
                      ) : (
                        <>
                          <Save className="h-5 w-5 mr-2" />
                          Valider et créer le bon de commande
                        </>
                      )}
                    </Button>

                    {/* Bouton créer facture - visible si client sélectionné */}
                    {selectedBuyerId && (
                      <Button
                        onClick={() => {
                          // TODO: Implémenter la création de facture
                          showInfo(`Fonctionnalité à venir: Création de facture ${selectedQuoteId ? `liée au devis #${selectedQuoteId}` : 'sans devis'}`);
                        }}
                        disabled={creating}
                        className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
                      >
                        <FileText className="h-5 w-5 mr-2" />
                        Créer une facture
                      </Button>
                    )}
                  </div>
                )}

                {result.status === 'created' && result.order && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => navigate(`/achat/orders`)}
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                    >
                      Voir les commandes
                    </Button>

                    {/* Bouton créer facture - toujours visible après création */}
                    <Button
                      onClick={() => {
                        // TODO: Implémenter la création de facture à partir du bon de commande
                        showInfo(`Fonctionnalité à venir: Création de facture à partir du bon de commande #${result.order.order_number}`);
                      }}
                      className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
                    >
                      <FileText className="h-5 w-5 mr-2" />
                      Créer une facture
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPurchaseOrderPDF;
